/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Zap, Server, Brain, Globe, MessageCircle, Database, TrendingUp, Shield, Lock, ShieldCheck, Bot
} from 'lucide-react';

// --- AI BRAIN DIAGRAM (New) ---
export const AiBrainDiagram: React.FC = () => {
    const iconVariants = {
        hidden: { scale: 0, opacity: 0 },
        visible: (i: number) => ({
            scale: 1,
            opacity: 1,
            transition: { delay: i * 0.2, type: 'spring', stiffness: 300, damping: 20 }
        })
    };

    return (
        <div className="h-[250px] bg-[#0a0a0a] rounded-xl p-6 flex items-center justify-center relative overflow-hidden border border-stone-800">
            {/* Background elements */}
            <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
            <motion.div 
                className="absolute inset-0 bg-radial-gradient-brain"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
            />
            
            {/* Central Node */}
            <motion.div
                className="w-20 h-20 bg-nobel-gold/10 rounded-full flex items-center justify-center border-2 border-nobel-gold/30 relative"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
            >
                <Brain size={32} className="text-nobel-gold z-10" />
                <motion.div 
                    className="absolute inset-0 border-2 border-nobel-gold rounded-full"
                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                />
            </motion.div>
            
            {/* Input Nodes */}
            {[
                { icon: Globe, pos: { top: '10%', left: '15%' } },
                { icon: MessageCircle, pos: { top: '40%', left: '5%' } },
                { icon: Database, pos: { top: '70%', left: '15%' } },
            ].map((node, i) => (
                <motion.div 
                    key={i} 
                    className="absolute w-10 h-10 bg-stone-800 rounded-full flex items-center justify-center border border-stone-700"
                    style={node.pos}
                    variants={iconVariants}
                    initial="hidden"
                    animate="visible"
                    custom={i + 2}
                >
                    <node.icon size={16} className="text-stone-400" />
                </motion.div>
            ))}

            {/* Output Node */}
            <motion.div 
                className="absolute w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center border-2 border-green-500/30"
                style={{ top: '40%', right: '5%' }}
                variants={iconVariants}
                initial="hidden"
                animate="visible"
                custom={5}
            >
                <TrendingUp size={20} className="text-green-400" />
            </motion.div>
            
            {/* Animated Connections (Simplified using SVG offsetPath) */}
            <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
                <defs>
                    <path id="path1" d="M 60 125 C 80 100, 100 100, 125 125" stroke="none" fill="none" />
                    <path id="path2" d="M 40 90 C 70 100, 90 110, 125 125" stroke="none" fill="none" />
                    <path id="path3" d="M 60 180 C 80 160, 100 150, 125 125" stroke="none" fill="none" />
                    <path id="path4" d="M 125 125 C 160 100, 190 110, 220 100" stroke="none" fill="none" />
                </defs>
            </svg>
             <motion.div 
                className="absolute w-1 h-1 bg-nobel-gold rounded-full"
                style={{ offsetPath: `path("M 40 90 C 70 100, 90 110, 125 125")`}}
                animate={{ offsetDistance: ["0%", "100%"] }}
                transition={{ repeat: Infinity, duration: 2.2, ease: 'linear', delay: 1 }}
            />
            <motion.div 
                className="absolute w-1 h-1 bg-nobel-gold rounded-full"
                style={{ offsetPath: `path("M 125 125 C 160 100, 190 110, 220 100")`}}
                animate={{ offsetDistance: ["0%", "100%"] }}
                transition={{ repeat: Infinity, duration: 2, ease: 'linear', delay: 0.5 }}
            />
        </div>
    );
};

// --- SECURITY DIAGRAM (Radar Style) ---
export const SecurityDiagram: React.FC = () => {
    const icons = [
        { icon: Lock, angle: 45 },
        { icon: Bot, angle: 135 },
        { icon: Server, angle: 225 },
        { icon: Zap, angle: 315 },
    ];

    return (
        <div className="h-[250px] bg-white rounded-xl p-6 flex items-center justify-center relative overflow-hidden">
            <div className="relative w-48 h-48 flex items-center justify-center">
                
                {/* Concentric Rings */}
                {[0.4, 0.7, 1.0].map(scale => (
                    <motion.div 
                        key={scale}
                        className="absolute rounded-full border border-dashed border-stone-200"
                        style={{ width: `${scale * 100}%`, height: `${scale * 100}%` }}
                    />
                ))}

                {/* Radar Sweep */}
                <motion.div
                    className="absolute w-full h-full origin-center"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                >
                    <div className="absolute top-0 left-1/2 w-[1px] h-1/2 bg-gradient-to-b from-nobel-gold/50 to-transparent" />
                </motion.div>
                
                {/* Central Shield */}
                <div className="w-20 h-20 bg-nobel-gold/10 rounded-full flex items-center justify-center border-2 border-nobel-gold/20">
                    <ShieldCheck size={32} className="text-nobel-gold" />
                </div>
                
                {/* Static Icons on Rings */}
                {icons.map(({ icon: Icon, angle }, i) => {
                    const radius = 96; // 48 (half of width) * 2
                    const x = Math.cos((angle - 90) * (Math.PI / 180)) * radius * 0.7;
                    const y = Math.sin((angle - 90) * (Math.PI / 180)) * radius * 0.7;
                    return (
                        <div 
                            key={i} 
                            className="absolute top-1/2 left-1/2 w-8 h-8 bg-white border border-stone-200 rounded-full flex items-center justify-center shadow-sm"
                            style={{ transform: `translate(-50%, -50%) translate(${x}px, ${y}px)` }}
                        >
                            <Icon size={14} className="text-stone-600" />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- SCALE & SPEED: PARALLEL EXECUTION ENGINE (Retained and slightly restyled) ---
export const SurfaceCodeDiagram: React.FC = () => {
  const [lanes, setLanes] = useState([
      { id: 1, load: 30, delay: 0 },
      { id: 2, load: 80, delay: 0.5 },
      { id: 3, load: 45, delay: 1.2 },
      { id: 4, load: 90, delay: 0.8 },
  ]);

  useEffect(() => {
      const interval = setInterval(() => {
          setLanes(prev => prev.map(lane => ({
              ...lane,
              load: Math.min(100, Math.max(10, lane.load + (Math.random() * 30 - 15))) 
          })));
      }, 2000); 
      return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-[250px] flex flex-col p-6 bg-[#0a0a0a] rounded-xl shadow-soft-xl border border-stone-800 relative overflow-hidden group">
      {/* Metallic Gold sheen background */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-[#1a1a1a] to-stone-900 z-0"></div>
      <div className="absolute top-0 right-0 w-64 h-64 bg-nobel-gold/5 rounded-full blur-[80px] pointer-events-none"></div>
      
      {/* Header */}
      <div className="flex justify-between items-center mb-4 relative z-10">
          <div className="text-right">
              <div className="text-2xl font-mono font-bold text-nobel-gold tracking-tighter">4,820</div>
              <div className="text-[9px] uppercase text-stone-600 font-bold tracking-widest">TPS Live</div>
          </div>
           <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-[10px] text-green-500 font-bold tracking-wider">OPTIMAL</span>
          </div>
      </div>
      
      {/* Lanes */}
      <div className="flex-1 flex flex-col justify-center gap-2 relative z-10">
         {lanes.map((lane) => (
             <div key={lane.id} className="relative h-8 bg-stone-900/50 rounded-md border border-stone-800/50 flex items-center px-2 shadow-inner overflow-hidden">
                <div className="flex-1 h-full relative mx-2 flex items-center">
                    <div className="absolute inset-0 w-full h-[1px] bg-stone-800 top-1/2"></div>
                    <motion.div
                        className="absolute h-1 rounded-full bg-gradient-to-r from-transparent via-nobel-gold to-white shadow-[0_0_8px_#C5A059]"
                        style={{ top: '50%', marginTop: '-2px', width: '40px' }}
                        animate={{ left: ['-10%', '110%'] }}
                        transition={{ 
                            repeat: Infinity, 
                            duration: 10 - (lane.load / 20),
                            ease: "linear",
                            delay: lane.delay
                        }}
                    />
                </div>
                <div className="w-8 text-right">
                    <div className="h-1 w-full bg-stone-800 rounded-full overflow-hidden">
                        <motion.div 
                            className="h-full bg-nobel-gold"
                            animate={{ width: `${lane.load}%` }}
                            transition={{ duration: 0.5 }}
                        />
                    </div>
                </div>
             </div>
         ))}
      </div>
    </div>
  );
};